/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors } from '../../../shared/theme/ticketColors';
import type { BranchAccess } from '../../../shell/session/store/sessionTypes';
import { ModuleEmptyState } from '../../../shared/ui/ModuleEmptyState';
import { BranchSelectionRow } from './BranchSelectionRow';
import { formatBranchLocation } from './branchSelectionUi';

type EmptyStateKind = 'owner' | 'employee';

type Props = {
  branches: BranchAccess[];
  cashSessionByBranch: Record<string, boolean>;
  branchesLoading: boolean;
  emptyStateKind?: EmptyStateKind;
  /** True cuando la lista está vacía por un filtro de búsqueda (no por falta real de sucursales). */
  hasSearchQuery?: boolean;
  onCreateBranch: () => void;
  onSelectBranch: (branchId: string) => void;
  variant: 'all' | 'others';
};

export function BranchListSection({
  branches,
  cashSessionByBranch,
  branchesLoading,
  emptyStateKind = 'owner',
  hasSearchQuery = false,
  onCreateBranch,
  onSelectBranch,
  variant,
}: Props) {
  const sectionTitle = variant === 'others' ? 'Otras sucursales' : 'Sucursales';

  if (branchesLoading && branches.length === 0) {
    return <ModuleEmptyState loading loadingText="Cargando sucursales…" />;
  }

  if (branches.length === 0) {
    if (hasSearchQuery) {
      return (
        <ModuleEmptyState
          title="No encontramos sucursales"
          subtitle="Probá con otro nombre o dirección."
        />
      );
    }

    const isEmployeeEmpty = emptyStateKind === 'employee';
    return (
      <ModuleEmptyState
        title={isEmployeeEmpty ? 'No tenés sucursales asignadas' : 'Todavía no hay sucursales'}
        subtitle={
          isEmployeeEmpty
            ? 'Pedile al dueño que te habilite acceso a una sucursal.'
            : 'Creá la primera sucursal para empezar a vender y cargar stock.'
        }
        primaryAction={!isEmployeeEmpty ? { label: 'Crear sucursal', onPress: onCreateBranch } : undefined}
      />
    );
  }

  return (
    <>
      <Text style={localStyles.sectionTitle}>{sectionTitle}</Text>
      <View style={{ gap: spacing.sm }}>
        {branches.map((branch) => {
          const location = formatBranchLocation(branch.location);
          const cashOpen = branch.id in cashSessionByBranch ? cashSessionByBranch[branch.id] : null;
          const description = [
            location ?? 'Sin dirección cargada',
            branch.permissionsOfflineUnknown ? 'Permisos sin sincronizar' : null,
          ]
            .filter(Boolean)
            .join(' · ');

          return (
            <BranchSelectionRow
              key={branch.id}
              title={branch.name}
              description={description}
              cashOpen={cashOpen}
              onPress={() => onSelectBranch(branch.id)}
              tone={variant === 'others' ? 'accent' : 'default'}
              testID="auth.branch-row"
            />
          );
        })}
      </View>
    </>
  );
}

const localStyles = StyleSheet.create({
  sectionTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    marginBottom: 0,
  },
});
